import { useTranslation } from '../utils/i18n';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppContext } from '../context/AppContext';
import type { Prefs } from '../types/book';
import { getBook, updateBook } from '../utils/books';
import {
  addAndroidBackButtonListener,
  addNativeAppStateListener,
  addNativeUrlOpenListener,
  closeAuthBrowser,
  completeNativeSessionTicket,
  configureNativeChrome,
  consumePendingNativeEpubFiles,
  exitNativeApp,
  getNativeLaunchUrl,
  isNativeApp,
} from '../utils/platform';
import { logger } from '../utils/logger';
import { parseNativeDebugUrl } from '../utils/nativeDebugRouting';
import { queuePendingSharedFiles } from '../utils/shareTarget';

const NativeRuntime: React.FC = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const { changePrefs, effectiveTheme, prefs, setSnackbarMessage } = useContext(AppContext);
  const handledUrlsRef = useRef<Set<string>>(new Set());
  const isProcessingAuthRef = useRef(false);
  const isProcessingFilesRef = useRef(false);

  const handlePendingNativeFiles = useCallback(async () => {
    if (!isNativeApp() || isProcessingFilesRef.current) {
      return;
    }

    isProcessingFilesRef.current = true;
    try {
      const files = await consumePendingNativeEpubFiles();
      if (files.length === 0) {
        return;
      }

      queuePendingSharedFiles(files);
      logger.info('Queued native EPUB files for import', {
        count: files.length,
        names: files.map((file) => file.name),
      });
      navigate('/handle_epub');
    } finally {
      isProcessingFilesRef.current = false;
    }
  }, [navigate]);

  const handleNativeUrl = useCallback(
    async (rawUrl: string | null | undefined) => {
      if (!rawUrl || !isNativeApp() || handledUrlsRef.current.has(rawUrl)) {
        return;
      }

      let parsedUrl: URL;
      try {
        parsedUrl = new URL(rawUrl);
      } catch (error) {
        logger.warn('Ignoring malformed native URL', {
          rawUrl,
          error: error instanceof Error ? error.message : String(error),
        });
        return;
      }

      const debugRoute = parseNativeDebugUrl(rawUrl);
      if (debugRoute) {
        handledUrlsRef.current.add(rawUrl);
        logger.info('Handling native debug route', debugRoute);

        const nextPrefs: Partial<Prefs> = {};
        if (debugRoute.theme) {
          nextPrefs.theme = debugRoute.theme;
        }
        if (debugRoute.nativeImmersiveMode !== null) {
          nextPrefs.nativeImmersiveMode = debugRoute.nativeImmersiveMode;
        }
        if (Object.keys(nextPrefs).length > 0) {
          changePrefs(nextPrefs);
        }

        if (debugRoute.bookId && debugRoute.flow) {
          const currentBook = getBook(debugRoute.bookId);
          if (currentBook) {
            void updateBook(
              {
                ...currentBook,
                settings: {
                  ...currentBook.settings,
                  flow: debugRoute.flow,
                },
              },
              false,
            );
          } else {
            logger.warn('Native debug route referenced a missing book', {
              bookId: debugRoute.bookId,
            });
          }
        }

        navigate(debugRoute.path);
        return;
      }

      if (parsedUrl.protocol === 'wereader:' && parsedUrl.hostname === 'drive') {
        handledUrlsRef.current.add(rawUrl);
        const status = parsedUrl.searchParams.get('status');
        logger.info('Handling native Google Drive callback URL', {
          status: status || '(missing)',
          platform: parsedUrl.searchParams.get('platform') || '(missing)',
        });
        if (status === 'connected') {
          setSnackbarMessage({
            text: t('DRIVE_CONNECTED'),
            duration: 4000,
          });
          window.dispatchEvent(new CustomEvent('wereader:drive-connected'));
          window.dispatchEvent(new CustomEvent('wereader:local-data-changed', { detail: { reason: 'drive-connected' } }));
        } else if (status === 'mismatch') {
          setSnackbarMessage({
            text: t('DRIVE_ACCOUNT_MISMATCH'),
            duration: 6000,
          });
        } else if (status === 'scope') {
          setSnackbarMessage({
            text: t('DRIVE_PERMISSION_REQUIRED'),
            duration: 6000,
          });
        } else {
          setSnackbarMessage({
            text: t('DRIVE_CONNECTION_FAILED'),
            duration: 5000,
          });
        }
        await closeAuthBrowser();
        return;
      }

      if (parsedUrl.protocol !== 'wereader:' || parsedUrl.hostname !== 'auth') {
        return;
      }

      handledUrlsRef.current.add(rawUrl);
      const ticket = parsedUrl.searchParams.get('ticket');
      if (!ticket) {
        logger.warn('Received native auth URL without a ticket', {
          rawUrl,
        });
        return;
      }

      if (isProcessingAuthRef.current) {
        logger.debug('Ignoring duplicate native auth ticket while another exchange is running');
        return;
      }

      isProcessingAuthRef.current = true;
      try {
        await closeAuthBrowser();
        const user = await completeNativeSessionTicket(ticket);
        if (!user) {
          setSnackbarMessage({
            text: t('LOGIN_FAILED'),
            duration: 4000,
          });
          return;
        }

        logger.info('Completed native auth ticket exchange', {
          email: user.email,
        });
        window.location.replace(import.meta.env.BASE_URL || '/');
      } catch (error) {
        logger.error('Failed to complete native auth ticket exchange', {
          error: error instanceof Error ? error.message : String(error),
        });
        setSnackbarMessage({
          text: t('LOGIN_FAILED'),
          duration: 4000,
        });
      } finally {
        isProcessingAuthRef.current = false;
      }
    },
    [changePrefs, navigate, setSnackbarMessage, t],
  );

  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }

    void configureNativeChrome({
      effectiveTheme,
      nativeImmersiveMode: prefs?.nativeImmersiveMode,
    });
  }, [effectiveTheme, prefs?.nativeImmersiveMode]);

  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }

    let isCancelled = false;
    let urlHandlePromise: Promise<{ remove: () => Promise<void> } | null> | null = null;
    let appStateHandlePromise: Promise<{ remove: () => Promise<void> } | null> | null = null;

    void (async () => {
      const launchUrl = await getNativeLaunchUrl();
      if (!isCancelled) {
        await handleNativeUrl(launchUrl);
        await handlePendingNativeFiles();
      }
    })();

    urlHandlePromise = addNativeUrlOpenListener(async (url) => {
      if (isCancelled) {
        return;
      }
      await handleNativeUrl(url);
    });

    appStateHandlePromise = addNativeAppStateListener(async (isActive) => {
      if (isCancelled || !isActive) {
        return;
      }
      await configureNativeChrome({
        effectiveTheme,
        nativeImmersiveMode: prefs?.nativeImmersiveMode,
      });
      await handlePendingNativeFiles();
    });

    return () => {
      isCancelled = true;
      void urlHandlePromise?.then((handle) => handle?.remove());
      void appStateHandlePromise?.then((handle) => handle?.remove());
    };
  }, [effectiveTheme, handleNativeUrl, handlePendingNativeFiles, prefs?.nativeImmersiveMode]);

  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }

    let backButtonHandlePromise: Promise<{ remove: () => Promise<void> } | null> | null = null;
    backButtonHandlePromise = addAndroidBackButtonListener(async () => {
      const currentPath = window.location.pathname;
      if (currentPath !== '/') {
        navigate(-1);
        return;
      }

      await exitNativeApp();
    });

    return () => {
      void backButtonHandlePromise?.then((handle) => handle?.remove());
    };
  }, [navigate]);

  return null;
};

export default NativeRuntime;
